/**
 * SETTINGS PAGE - NOTIFICATIONS & EMAIL
 * 
 * Configure:
 * - Notification preferences
 * - Email server settings
 * - Test notifications
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/settings/')({  
  component: SettingsPage,
});
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Mail, Send, CheckCircle, AlertCircle, Settings as SettingsIcon } from 'lucide-react';
import { useNotificationPreferences, useUpdateNotificationPreferences, useCreateNotification } from '@/hooks/useNotifications';
import { useToast } from '@/hooks/use-toast';

export default function SettingsPage() {
  const { toast } = useToast();
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const createNotification = useCreateNotification();
  
  // Email server settings (would be stored in database in production)
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'leBusiness',
  });
  
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [testNotifSent, setTestNotifSent] = useState(false);
  
  const handlePreferenceChange = async (key: string, value: any) => {
    try {
      await updatePreferences.mutateAsync({
        ...preferences,
        [key]: value,
      });
      
      toast({
        title: 'Préférences mises à jour',
        description: 'Vos préférences de notification ont été sauvegardées.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour les préférences.',
        variant: 'destructive',
      });
    }
  };
  
  const handleTestEmail = async () => {
    if (!emailSettings.enabled) {
      toast({
        title: 'Email désactivé',
        description: 'Activez d\'abord le serveur email.',
        variant: 'destructive',
      });
      return;
    }
    
    // TODO: Implement actual email sending
    setTestEmailSent(true);
    toast({
      title: 'Email de test envoyé',
      description: `Un email de test a été envoyé à ${emailSettings.from_email}`,
    });
    
    setTimeout(() => setTestEmailSent(false), 3000);
  };
  
  const handleTestNotification = async () => {
    try {
      await createNotification.mutateAsync({
        type: 'info',
        priority: 'normal',
        title: '🎉 Notification de test',
        message: 'Ceci est une notification de test. Le système fonctionne correctement!',
        send_in_app: true,
        send_email: false,
      });
      
      setTestNotifSent(true);
      toast({
        title: 'Notification créée',
        description: 'Vérifiez la cloche de notification dans la navbar.',
      });
      
      setTimeout(() => setTestNotifSent(false), 3000);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de créer la notification de test.',
        variant: 'destructive',
      });
    }
  };
  
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-8 w-8" />
            Paramètres / الإعدادات
          </h1>
          <p className="text-muted-foreground mt-2">
            Gérez vos notifications et paramètres email
          </p>
        </div>
        
        <Tabs defaultValue="notifications" className="space-y-6">
          <TabsList>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Serveur Email
            </TabsTrigger>
          </TabsList>
          
          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications" className="space-y-6">
            {/* Test Notification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-blue-500" />
                  Tester les Notifications
                </CardTitle>
                <CardDescription>
                  Envoyez une notification de test pour vérifier que le système fonctionne
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleTestNotification}
                  disabled={testNotifSent}
                  className="w-full sm:w-auto"
                >
                  {testNotifSent ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Notification envoyée!
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Envoyer une notification de test
                    </>
                  )}
                </Button>
                <p className="text-sm text-muted-foreground mt-3">
                  💡 La notification apparaîtra dans la cloche (🔔) en haut à droite
                </p>
              </CardContent>
            </Card>
            
            {/* Global Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Paramètres Généraux</CardTitle>
                <CardDescription>Activez ou désactivez toutes les notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enabled" className="text-base">
                      Activer les notifications
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des notifications dans l'application
                    </p>
                  </div>
                  <Switch
                    id="enabled"
                    checked={preferences?.enabled ?? true}
                    onCheckedChange={(checked) => handlePreferenceChange('enabled', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="weekend" className="text-base">
                      Notifications le weekend
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des notifications samedi et dimanche
                    </p>
                  </div>
                  <Switch
                    id="weekend"
                    checked={preferences?.weekend_notifications ?? false}
                    onCheckedChange={(checked) => handlePreferenceChange('weekend_notifications', checked)}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Channel Preferences */}
            <Card>
              <CardHeader>
                <CardTitle>Canaux de Notification</CardTitle>
                <CardDescription>Choisissez comment recevoir les notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email_enabled" className="text-base">
                      Notifications par Email
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des emails pour les notifications importantes
                    </p>
                  </div>
                  <Switch
                    id="email_enabled"
                    checked={preferences?.email_enabled ?? true}
                    onCheckedChange={(checked) => handlePreferenceChange('email_enabled', checked)}
                  />
                </div>
                
                {preferences?.email_enabled && (
                  <div className="ml-6 space-y-2">
                    <Label htmlFor="email_address">Adresse Email</Label>
                    <Input
                      id="email_address"
                      type="email"
                      placeholder="votre.email@exemple.com"
                      value={preferences?.email_address || ''}
                      onChange={(e) => handlePreferenceChange('email_address', e.target.value)}
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="sms_enabled" className="text-base">
                      Notifications SMS
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des SMS pour les urgences (bientôt disponible)
                    </p>
                  </div>
                  <Switch
                    id="sms_enabled"
                    checked={preferences?.sms_enabled ?? false}
                    onCheckedChange={(checked) => handlePreferenceChange('sms_enabled', checked)}
                    disabled
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="whatsapp_enabled" className="text-base">
                      Notifications WhatsApp
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir des messages WhatsApp (bientôt disponible)
                    </p>
                  </div>
                  <Switch
                    id="whatsapp_enabled"
                    checked={preferences?.whatsapp_enabled ?? false}
                    onCheckedChange={(checked) => handlePreferenceChange('whatsapp_enabled', checked)}
                    disabled
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Category Preferences */}
            <Card>
              <CardHeader>
                <CardTitle>Types de Notifications</CardTitle>
                <CardDescription>Choisissez quels types de notifications recevoir</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Compliance */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">📋 Conformité (Compliance)</Label>
                      <p className="text-sm text-muted-foreground">
                        Rappels des échéances fiscales (G50, TVA, etc.)
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.compliance_enabled ?? true}
                      onCheckedChange={(checked) => handlePreferenceChange('compliance_enabled', checked)}
                    />
                  </div>
                  {preferences?.compliance_enabled && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Recevoir par email</span>
                        <Switch
                          checked={preferences?.compliance_email ?? true}
                          onCheckedChange={(checked) => handlePreferenceChange('compliance_email', checked)}
                        />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Recevoir par SMS</span>
                        <Switch
                          checked={preferences?.compliance_sms ?? false}
                          onCheckedChange={(checked) => handlePreferenceChange('compliance_sms', checked)}
                          disabled
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Invoices */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">🧾 Factures (Invoices)</Label>
                      <p className="text-sm text-muted-foreground">
                        Notifications de création, paiement, retard
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.invoice_enabled ?? true}
                      onCheckedChange={(checked) => handlePreferenceChange('invoice_enabled', checked)}
                    />
                  </div>
                  {preferences?.invoice_enabled && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Recevoir par email</span>
                        <Switch
                          checked={preferences?.invoice_email ?? false}
                          onCheckedChange={(checked) => handlePreferenceChange('invoice_email', checked)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Stock */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">📦 Stock</Label>
                      <p className="text-sm text-muted-foreground">
                        Alertes de stock faible, rupture de stock
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.stock_enabled ?? true}
                      onCheckedChange={(checked) => handlePreferenceChange('stock_enabled', checked)}
                    />
                  </div>
                  {preferences?.stock_enabled && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Recevoir par email</span>
                        <Switch
                          checked={preferences?.stock_email ?? false}
                          onCheckedChange={(checked) => handlePreferenceChange('stock_email', checked)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Payments */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">💰 Paiements</Label>
                      <p className="text-sm text-muted-foreground">
                        Paiements reçus, échéances de chèques
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.payment_enabled ?? true}
                      onCheckedChange={(checked) => handlePreferenceChange('payment_enabled', checked)}
                    />
                  </div>
                </div>
                
                {/* System */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">⚙️ Système</Label>
                      <p className="text-sm text-muted-foreground">
                        Mises à jour, maintenance, nouvelles fonctionnalités
                      </p>
                    </div>
                    <Switch
                      checked={preferences?.system_enabled ?? true}
                      onCheckedChange={(checked) => handlePreferenceChange('system_enabled', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* EMAIL SERVER TAB */}
          <TabsContent value="email" className="space-y-6">
            {/* Test Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-green-500" />
                  Tester l'Email
                </CardTitle>
                <CardDescription>
                  Envoyez un email de test pour vérifier la configuration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleTestEmail}
                  disabled={!emailSettings.enabled || testEmailSent}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {testEmailSent ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Email envoyé!
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Envoyer un email de test
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Email Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Configuration du Serveur Email</CardTitle>
                <CardDescription>
                  Configurez votre serveur SMTP pour envoyer des emails de notification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Email */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                  <div>
                    <Label htmlFor="email_server_enabled" className="text-base font-semibold">
                      Activer le serveur email
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Permettre l'envoi d'emails de notification
                    </p>
                  </div>
                  <Switch
                    id="email_server_enabled"
                    checked={emailSettings.enabled}
                    onCheckedChange={(checked) => setEmailSettings({...emailSettings, enabled: checked})}
                  />
                </div>
                
                {/* SMTP Settings */}
                <div className={`space-y-4 ${!emailSettings.enabled && 'opacity-50 pointer-events-none'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_host">
                        Serveur SMTP
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="smtp_host"
                        placeholder="smtp.exemple.com"
                        value={emailSettings.smtp_host}
                        onChange={(e) => setEmailSettings({...emailSettings, smtp_host: e.target.value})}
                      />
                      <p className="text-xs text-muted-foreground">
                        Exemples: smtp.gmail.com, smtp.office365.com, smtp.sendgrid.net
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="smtp_port">
                        Port SMTP
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="smtp_port"
                        placeholder="587"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings({...emailSettings, smtp_port: e.target.value})}
                      />
                      <p className="text-xs text-muted-foreground">
                        587 (TLS) ou 465 (SSL)
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_user">
                        Nom d'utilisateur
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="smtp_user"
                        type="email"
                        placeholder="votre.email@exemple.com"
                        value={emailSettings.smtp_user}
                        onChange={(e) => setEmailSettings({...emailSettings, smtp_user: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="smtp_password">
                        Mot de passe
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        placeholder="••••••••"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings({...emailSettings, smtp_password: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="from_email">
                        Email expéditeur
                        <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="from_email"
                        type="email"
                        placeholder="no-reply@votre-domaine.com"
                        value={emailSettings.from_email}
                        onChange={(e) => setEmailSettings({...emailSettings, from_email: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="from_name">Nom expéditeur</Label>
                      <Input
                        id="from_name"
                        placeholder="leBusiness"
                        value={emailSettings.from_name}
                        onChange={(e) => setEmailSettings({...emailSettings, from_name: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={() => {
                        toast({
                          title: 'Configuration sauvegardée',
                          description: 'Les paramètres du serveur email ont été enregistrés.',
                        });
                      }}
                    >
                      Sauvegarder la configuration
                    </Button>
                  </div>
                </div>
                
                {/* Info */}
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        💡 Services Email Recommandés
                      </h4>
                      <ul className="mt-2 text-xs text-blue-800 dark:text-blue-200 space-y-1">
                        <li>• <strong>Gmail:</strong> smtp.gmail.com:587 (utilisez un App Password)</li>
                        <li>• <strong>Outlook/Office365:</strong> smtp.office365.com:587</li>
                        <li>• <strong>SendGrid:</strong> smtp.sendgrid.net:587 (recommandé pour production)</li>
                        <li>• <strong>Mailgun:</strong> smtp.mailgun.org:587</li>
                        <li>• <strong>Brevo (Sendinblue):</strong> smtp-relay.brevo.com:587</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
